import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Badge,
  Spinner,
  Alert,
  Modal,
  Form
} from 'react-bootstrap';
import { FaTrash, FaEdit } from 'react-icons/fa';

import dayjs from 'dayjs';
import {
  fetchCrmLeadsById,
  convertLeadToOpportunity
} from '../../../services/crmLeadsService';
import { deleteCrmActivity } from '../../../services/crmActivityService';
import { deleteCommunication } from '../../../services/crmCommService';
import { deleteCrmTask } from '../../../services/crmTaskService';
import { fetchUsers } from '../../../services/userService';
import { createCrmActivity } from '../../../services/crmActivityService';
import { createCrmCommunication } from '../../../services/crmCommService';
import { createCrmTask } from '../../../services/crmTaskService';
import Swal from 'sweetalert2';
import DocumentUploader from 'components/DocumentUploader';

const LeadDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const activityTypes = ['Call', 'Email', 'Meeting', 'Follow-up'];
  const communicationTypes = ['Email', 'Call', 'Meeting Note'];
  const taskStatuses = ['Pending', 'In Progress', 'Completed'];
  const priorities = ['High', 'Medium', 'Low'];
  const navigate = useNavigate();
  const [lead, setLead] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showCommunicationModal, setShowCommunicationModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [conversionLoading, setConversionLoading] = useState(false); // Loading state for conversion
  useEffect(() => {
    const fetchData = async () => {
      const leadData = await fetchCrmLeadsById(id);
      const userData = await fetchUsers();
      setLead(leadData);
      setUsers(userData);
      setLoading(false);
    };
    fetchData();
  }, [id]);
  const handleSubmit = async (
    type: string,
    data: any,
    setModalState: (state: boolean) => void
  ) => {
    setIsSubmitting(true);
    try {
      let response;
      if (type === 'activity') response = await createCrmActivity(data);
      else if (type === 'communication')
        response = await createCrmCommunication(data);
      else if (type === 'task') response = await createCrmTask(data);

      setMessage(response?.message || `${type} added successfully!`);
      const updatedLead = await fetchCrmLeadsById(id);
      setLead(updatedLead);
      setModalState(false);
    } catch (error) {
      setMessage(`Failed to add ${type}. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConvertToOpportunity = async () => {
    try {
      setConversionLoading(true); // Start loading

      // Call the service to convert the lead to an opportunity
      const response = await convertLeadToOpportunity(id);
      console.log('Conversion Response:', response);
      // const { data, loading } = useLeadDetails(leadId || '') || [];

      alert('Lead successfully converted to opportunity!');
      navigate(`/crm/opportunities/all-opportunities`);
      // Redirect to the opportunity details page
    } catch (error) {
      console.error('Failed to convert lead to opportunity:', error);
      alert('Failed to convert lead to opportunity. Please try again.');
    } finally {
      setConversionLoading(false); // Stop loading
    }
  };

  const handleDelete = async (type: string, typeId: string) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return; // Stop if user cancels

    setDeletingId(typeId); // Show loading state

    try {
      // Perform the delete operation
      if (type === 'activity') await deleteCrmActivity(typeId);
      else if (type === 'communication') await deleteCommunication(typeId);
      else if (type === 'task') await deleteCrmTask(typeId);

      // ✅ Update UI to remove deleted item
      setLead((prevLead: any) => ({
        ...prevLead,
        activities:
          type === 'activity'
            ? prevLead.activities.filter(
                (item: any) => item.activity_id !== typeId
              )
            : prevLead.activities,
        communications:
          type === 'communication'
            ? prevLead.communications.filter(
                (item: any) => item.communication_id !== typeId
              )
            : prevLead.communications,
        tasks:
          type === 'task'
            ? prevLead.tasks.filter((item: any) => item.task_id !== typeId)
            : prevLead.tasks
      }));

      // ✅ Show success alert after deletion
      await Swal.fire({
        title: 'Deleted!',
        text: `The ${
          type.charAt(0).toUpperCase() + type.slice(1)
        } has been removed successfully.`,
        icon: 'success'
      });
    } catch (error) {
      console.error('Delete Error:', error);
      await Swal.fire({
        title: 'Error!',
        text: 'Failed to delete the item.',
        icon: 'error'
      });
    }

    setDeletingId(null); // Reset loading state
  };

  if (loading)
    return <Spinner animation="border" className="d-block mx-auto" />;
  if (!lead) return <Alert variant="danger">Lead not found.</Alert>;

  return (
    <div className="container mt-4">
      <Card className="shadow p-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="fw-bold">{lead.natural_key}</h4>
            <div>
              <Button
                variant="primary"
                className="me-2"
                onClick={() => navigate(`/crm/leads/edit-lead/${id}`)}
              >
                <FaEdit /> Edit
              </Button>
              <Button
                variant="success"
                onClick={() => handleConvertToOpportunity()}
                disabled={conversionLoading}
              >
                {conversionLoading ? 'Converting...' : 'Convert to Opportunity'}
              </Button>
            </div>
          </div>
          <Badge bg="info" className="mb-3">
            {lead.status}
          </Badge>
          <p>
            <strong>Assigned To:</strong>{' '}
            {`${lead.user?.username} - ${lead.user?.email} - ${lead.user?.role}` ||
              'N/A'}
          </p>
          <p>
            <strong>Created At:</strong>{' '}
            {dayjs(lead.created_at).format('DD/MM/YYYY HH:mm:ss')}
          </p>
        </Card.Body>
      </Card>

      <DocumentUploader relatedToId="1001" relatedToType="CRM" />
      {/* 🔹 Action Buttons */}
      <div className="d-flex gap-3 mt-4">
        <Button variant="success" onClick={() => setShowActivityModal(true)}>
          ➕ Add Activity
        </Button>
        <Button
          variant="primary"
          onClick={() => setShowCommunicationModal(true)}
        >
          📞 Add Communication
        </Button>
        <Button variant="warning" onClick={() => setShowTaskModal(true)}>
          📝 Add Task
        </Button>
      </div>
      {/* 🔹 Activity Log */}
      <Card className="mt-4 shadow">
        <Card.Body>
          <h5 className="fw-bold">Activity Log</h5>
          {lead.activities?.length > 0 ? (
            lead.activities.map((activity: any) => (
              <div
                key={activity.activity_id}
                className="d-flex justify-content-between border-bottom py-3"
              >
                <div>
                  <Badge bg="success">{activity.activity_type}</Badge>
                  <p className="mb-1">{activity.description}</p>
                  <p>
                    <strong>Assigned To:</strong>{' '}
                    {users.find(user => user.user_id === activity.assigned_to)
                      ?.username || 'N/A'}
                  </p>
                  <small className="text-muted">
                    Date:{' '}
                    {dayjs(activity.activity_date).format(
                      'DD/MM/YYYY HH:mm:ss'
                    )}
                  </small>
                </div>
                <Button
                  variant="danger"
                  size="sm"
                  disabled={deletingId === activity.activity_id}
                  onClick={() => handleDelete('activity', activity.activity_id)}
                >
                  {deletingId === activity.activity_id ? (
                    <Spinner size="sm" />
                  ) : (
                    <FaTrash />
                  )}
                </Button>
              </div>
            ))
          ) : (
            <p className="text-muted">No Activities Found</p>
          )}
        </Card.Body>
      </Card>

      {/* 🔹 Communication Log */}
      <Card className="mt-4 shadow">
        <Card.Body>
          <h5 className="fw-bold">Communication Log</h5>
          {lead.communications?.length > 0 ? (
            lead.communications.map((comm: any) => (
              <div
                key={comm.communication_id}
                className="d-flex justify-content-between border-bottom py-3"
              >
                <div>
                  <Badge bg="primary">{comm.communication_type}</Badge>
                  <p>{comm.details}</p>
                </div>
                <Button
                  variant="danger"
                  size="sm"
                  disabled={deletingId === comm.communication_id}
                  onClick={() =>
                    handleDelete('communication', comm.communication_id)
                  }
                >
                  {deletingId === comm.communication_id ? (
                    <Spinner size="sm" />
                  ) : (
                    <FaTrash />
                  )}
                </Button>
              </div>
            ))
          ) : (
            <p className="text-muted">No Communications Found</p>
          )}
        </Card.Body>
      </Card>

      {/* 🔹 Task Log */}
      <Card className="mt-4 shadow">
        <Card.Body>
          <h5 className="fw-bold">Tasks</h5>
          {lead.tasks?.length > 0 ? (
            lead.tasks.map((task: any) => (
              <div
                key={task.id}
                className="d-flex justify-content-between border-bottom py-3"
              >
                <div>
                  <Badge bg="warning">{task.status}</Badge>
                  <p>{task.task_description}</p>
                  <small className="text-muted">
                    Due: {dayjs(task.due_date).format('DD/MM/YYYY')}
                  </small>
                </div>
                <Button
                  variant="danger"
                  size="sm"
                  disabled={deletingId === task.task_id}
                  onClick={() => handleDelete('task', task.task_id)}
                >
                  {deletingId === task.task_id ? (
                    <Spinner size="sm" />
                  ) : (
                    <FaTrash />
                  )}
                </Button>
              </div>
            ))
          ) : (
            <p className="text-muted">No Tasks Found</p>
          )}
        </Card.Body>
      </Card>

      {/* 🔹 Modals */}
      {/* Activity Modal */}
      <Modal
        show={showActivityModal}
        onHide={() => setShowActivityModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Activity</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Activity Type</Form.Label>
            <Form.Select id="activity_type">
              <option value="">Select Type</option>
              {activityTypes.map(type => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>Activity Date</Form.Label>
            <Form.Control id="activity_date" type="datetime-local" />
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>Description</Form.Label>
            <Form.Control id="activity_description" as="textarea" rows={3} />
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>Assigned To</Form.Label>
            <Form.Select id="activity_assigned">
              <option value="">Select User</option>
              {users.map(user => (
                <option key={user.user_id} value={user.user_id}>
                  {user.username}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              const data = {
                related_to_id: id,
                related_to_type: 'Lead',
                activity_type: (
                  document.getElementById('activity_type') as HTMLSelectElement
                ).value,
                activity_date: (
                  document.getElementById('activity_date') as HTMLInputElement
                ).value,
                description: (
                  document.getElementById(
                    'activity_description'
                  ) as HTMLTextAreaElement
                ).value,
                assigned_to: (
                  document.getElementById(
                    'activity_assigned'
                  ) as HTMLSelectElement
                ).value
              };
              handleSubmit('activity', data, setShowActivityModal);
            }}
          >
            Save Activity
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Communication Modal */}
      <Modal
        show={showCommunicationModal}
        onHide={() => setShowCommunicationModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Communication</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Communication Type</Form.Label>
            <Form.Select id="communication_type">
              <option value="">Select Type</option>
              {communicationTypes.map(type => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>Details</Form.Label>
            <Form.Control id="communication_details" as="textarea" rows={3} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              const data = {
                related_to_id: id,
                related_to_type: 'Lead',
                communication_type: (
                  document.getElementById(
                    'communication_type'
                  ) as HTMLSelectElement
                ).value,
                details: (
                  document.getElementById(
                    'communication_details'
                  ) as HTMLTextAreaElement
                ).value
              };
              handleSubmit('communication', data, setShowCommunicationModal);
            }}
          >
            Save Communication
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 🔹 Task Modal */}
      <Modal
        show={showTaskModal}
        onHide={() => setShowTaskModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Task</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Task Name</Form.Label>
            <Form.Control
              id="task_name"
              type="text"
              placeholder="Enter Task Name"
              required
            />
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              id="task_description"
              as="textarea"
              rows={3}
              required
            />
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>Status</Form.Label>
            <Form.Select id="task_status">
              <option value="">Select Status</option>
              {taskStatuses.map(status => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>Priority</Form.Label>
            <Form.Select id="task_priority">
              <option value="">Select Priority</option>
              {priorities.map(priority => (
                <option key={priority} value={priority}>
                  {priority}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>Due Date</Form.Label>
            <Form.Control id="task_due_date" type="date" required />
          </Form.Group>
          <Form.Group className="mt-3">
            <Form.Label>Assigned To</Form.Label>
            <Form.Select id="task_assigned">
              <option value="">Select User</option>
              {users.map(user => (
                <option key={user.user_id} value={user.user_id}>
                  {user.username}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              const data = {
                related_to_id: id,
                related_to_type: 'Lead',
                task_name: (
                  document.getElementById('task_name') as HTMLInputElement
                ).value,
                task_description: (
                  document.getElementById(
                    'task_description'
                  ) as HTMLTextAreaElement
                ).value,
                status: (
                  document.getElementById('task_status') as HTMLSelectElement
                ).value,
                priority: (
                  document.getElementById('task_priority') as HTMLSelectElement
                ).value,
                due_date: (
                  document.getElementById('task_due_date') as HTMLInputElement
                ).value,
                assigned_to: (
                  document.getElementById('task_assigned') as HTMLSelectElement
                ).value
              };
              handleSubmit('task', data, setShowTaskModal);
            }}
          >
            Save Task
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LeadDetails;
